"use strict";
/**
 * Exposes GroupLayerList class and related functions.
 * @module
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertLayer = exports.getGroupsFromCreateMapItem = void 0;
var tslib_1 = require("tslib");
var LayerList_1 = tslib_1.__importDefault(require("esri/dijit/LayerList"));
var metadataUtils_1 = require("./metadataUtils");
tslib_1.__exportStar(require("./searchUtils"), exports);
tslib_1.__exportStar(require("./conversionUtils"), exports);
tslib_1.__exportStar(require("./metadataUtils"), exports);
tslib_1.__exportStar(require("./types"), exports);
/**
 * Gets layer group definition (if present) from a web map.
 * @param itemInfo
 */
function getGroupsFromCreateMapItem(itemInfo) {
    if (itemInfo.itemData.applicationProperties) {
        var applicationProperties = itemInfo.itemData.applicationProperties;
        if (applicationProperties.viewing) {
            var viewing = applicationProperties.viewing;
            if (viewing.layerGrouping) {
                var layerGrouping = viewing.layerGrouping;
                if (layerGrouping && layerGrouping.enabled && layerGrouping.groups) {
                    return layerGrouping.groups;
                }
            }
        }
    }
    return null;
}
exports.getGroupsFromCreateMapItem = getGroupsFromCreateMapItem;
/**
 * Converts the operational layer format returned from arcgis/utils.createMap to the
 * operation layer format expected by the LayerList constructor.
 * @param opLayer Operational layer object from arcgis/utils.createMap complete event.
 * @returns Operational layer for use with the LayerList constructor.
 */
function convertLayer(opLayer) {
    var id = opLayer.id, title = opLayer.title, visibility = opLayer.visibility;
    var showLegend = true;
    var showOpacitySlider = true;
    var layer = opLayer.layerObject;
    // Don't show sublayers tab if there is only one sublayer. Otherwise, show it.
    var showSubLayers = layer.layerInfos
        ? layer.layerInfos.length > 1
        : true;
    return {
        id: id,
        title: title,
        layer: layer,
        visibility: visibility,
        showLegend: showLegend,
        showOpacitySlider: showOpacitySlider,
        showSubLayers: showSubLayers
    };
}
exports.convertLayer = convertLayer;
/**
 * Enumerate through the layers in the order presented in the grouping object.
 * @param titleGroups Grouping of layers
 * @param layers Array of layers
 * @param propertyName The name of the property. (Defaults to "id")
 * @param throwOnValueNotFound Set to true to throw a ReferenceError if a
 * group has a value that has no matching layer. If set to false, the invalid
 * value will simply be ignored.
 * @throws ReferenceError thrown if titleGroups contains a value that has no
 * corresponding layer when throwOnValueNotFound is true.
 * @yields {LayerListOperationalLayer}
 */
function enumerateLayersInGroupOrder(titleGroups, layers, propertyName, throwOnValueNotFound) {
    var _a, _b, _i, groupName, titles, _loop_1, titles_1, titles_1_1, title, e_1_1;
    var e_1, _c;
    if (propertyName === void 0) { propertyName = "id"; }
    return tslib_1.__generator(this, function (_d) {
        switch (_d.label) {
            case 0:
                _a = [];
                for (_b in titleGroups)
                    _a.push(_b);
                _i = 0;
                _d.label = 1;
            case 1:
                if (!(_i < _a.length)) return [3 /*break*/, 10];
                groupName = _a[_i];
                if (!titleGroups.hasOwnProperty(groupName)) return [3 /*break*/, 9];
                titles = titleGroups[groupName];
                _loop_1 = function (title) {
                    var layer, msg;
                    return tslib_1.__generator(this, function (_e) {
                        switch (_e.label) {
                            case 0:
                                layer = layers.find(function (l) { return l[propertyName] === title; });
                                if (!layer) {
                                    msg = "No layer found with \"".concat(propertyName, "\" of \"").concat(title, "\".");
                                    if (throwOnValueNotFound) {
                                        throw new ReferenceError(msg);
                                    }
                                    else {
                                        console.warn(msg);
                                        return [2 /*return*/, "continue"];
                                    }
                                }
                                return [4 /*yield*/, layer];
                            case 1:
                                _e.sent();
                                return [2 /*return*/];
                        }
                    });
                };
                _d.label = 2;
            case 2:
                _d.trys.push([2, 7, 8, 9]);
                titles_1 = (e_1 = void 0, tslib_1.__values(titles)), titles_1_1 = titles_1.next();
                _d.label = 3;
            case 3:
                if (!!titles_1_1.done) return [3 /*break*/, 6];
                title = titles_1_1.value;
                return [5 /*yield**/, _loop_1(title)];
            case 4:
                _d.sent();
                _d.label = 5;
            case 5:
                titles_1_1 = titles_1.next();
                return [3 /*break*/, 3];
            case 6: return [3 /*break*/, 9];
            case 7:
                e_1_1 = _d.sent();
                e_1 = { error: e_1_1 };
                return [3 /*break*/, 9];
            case 8:
                try {
                    if (titles_1_1 && !titles_1_1.done && (_c = titles_1.return)) _c.call(titles_1);
                }
                finally { if (e_1) throw e_1.error; }
                return [7 /*endfinally*/];
            case 9:
                _i++;
                return [3 /*break*/, 1];
            case 10: return [2 /*return*/];
        }
    });
}
/**
 * Creates a mapping of layer titles to group names.
 * @param groups group definition object.
 */
function createTitleToGroupMapping(groups) {
    var mapping = new Map();
    var _loop_2 = function (groupName) {
        if (groups.hasOwnProperty(groupName)) {
            var layerNames = groups[groupName];
            layerNames.forEach(function (ln) { return mapping.set(ln, groupName); });
        }
    };
    for (var groupName in groups) {
        _loop_2(groupName);
    }
    return mapping;
}
/**
 * Adds group headings above the list item corresponding to the first layer
 * in a group.
 * @param srcNode the srcNode of a LayerList
 * @param groups Defines which layers are in which group.
 * @returns CSS text to be added to a style element to be added to the page head.
 */
function addGroupHeadings(srcNode, groups) {
    // srcNode can be defined as either a node's "id" or the node itself.
    // Get the HTMLElement.
    var rootNode = typeof srcNode === "string"
        ? document.getElementById(srcNode)
        : srcNode;
    // Get the root node by ID in control creation changed node root to different node type
    // and is no longer using the input root node.
    if (rootNode.id) {
        rootNode = document.getElementById(rootNode.id) || rootNode;
    }
    // Get all of the <li> elements that correspond to map layers.
    var listItems = rootNode.querySelectorAll(".esriList > li");
    // Create layer title-to-group mapping.
    var mapping = createTitleToGroupMapping(groups);
    // Initialize variable to hold previous group name from previous loop iteration.
    var previousGroupName;
    // Define a common css class for all list items that begin a group.
    var groupStartClass = "layer-list-group-start";
    // Initialize group counter
    var groupNo = 0;
    // Initialize CSS class name to group name mapping.
    var classToGroupNameMapping = new Map();
    // Assign CSS classes to the <li>s corresponding to the first layer in a group.
    // For each such item, add item to the class-to-group mapping.
    Array.from(listItems, function (li) {
        var label = li.querySelector("label");
        if (label) {
            var title = label.textContent;
            var groupName = mapping.get(title) || "Other";
            if (groupName !== previousGroupName) {
                var className = "layer-list-group-".concat(groupNo);
                classToGroupNameMapping.set(className, groupName);
                li.classList.add(groupStartClass, className);
                groupNo++;
            }
            previousGroupName = groupName;
        }
    });
    // Create the CSS definition.
    var cssText = Array.from(classToGroupNameMapping, function (_a) {
        var _b = tslib_1.__read(_a, 2), className = _b[0], groupName = _b[1];
        return ".".concat(className, ":before { content: '").concat(groupName, "' }");
    });
    return cssText.join("\n");
}
/**
 * An extension of esri/dijit/LayerList that supports grouping.
 */
var GroupedLayerList = /** @class */ (function (_super) {
    tslib_1.__extends(GroupedLayerList, _super);
    function GroupedLayerList(options, srcNode) {
        var _this = this;
        // If options.groups is defined, sort the layers to match group definition
        if (options.groups) {
            var originLayers = options.layers;
            var sortedLayers_1 = new (Array.bind.apply(Array, tslib_1.__spreadArray([void 0], tslib_1.__read(enumerateLayersInGroupOrder(options.groups, options.layers, options.groupProperty, options.throwOnGroupNotFound)), false)))();
            // Append any layers that are not listed in the grouping object.
            originLayers
                .filter(function (l) { return !sortedLayers_1.includes(l); })
                .forEach(function (l) { return sortedLayers_1.push(l); });
            // Change the layers' order to match the group object.
            options.layers = sortedLayers_1.reverse();
        }
        // Get the "id" of the srcNode.
        var nodeId = srcNode === "string"
            ? srcNode
            : srcNode instanceof HTMLElement
                ? srcNode.id
                : null;
        _this = _super.call(this, options, srcNode) || this;
        // Get the node "id" if one was autogenerated.
        _this._nodeId =
            nodeId || typeof srcNode === "string"
                ? srcNode
                : srcNode.id;
        if (options.groups) {
            _this.on("load", function (evt) {
                // Add group headers to the first layer's <li> using CSS :before pseudo-element.
                var cssText = addGroupHeadings(srcNode, options.groups);
                var styleNode = document.createElement("style");
                styleNode.textContent = cssText;
                document.head.appendChild(styleNode);
            });
        }
        if (options.metadata) {
            (0, metadataUtils_1.addMetadataTabs)(_this, options.metadataOptions);
        }
        return _this;
    }
    Object.defineProperty(GroupedLayerList.prototype, "root", {
        /**
         * Gets the element that is hosting the control.
         */
        get: function () {
            return document.getElementById(this._nodeId);
        },
        enumerable: false,
        configurable: true
    });
    return GroupedLayerList;
}(LayerList_1.default));
exports.default = GroupedLayerList;
